import React from 'react';
import { Button, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@heroui/react";
import LoginRequiredModal from './Components/LogInRequiredModal';
import { Expand, Lock, RefreshCcw, X } from 'lucide-react';

const CHUNK = 64;
const BASE_FREE_DRAW_MAX_PIXELS = 25;
const GUEST_FREE_DRAW_MAX_PIXELS = 5;
const PREMIUM_COLOR_COSTS = {
  charcoal: 2000,
  silver: 2000,
  ivory: 2000,
  brick: 2000,
  amber: 2000,
  lemon: 2000,
  lime: 2000,
  royal: 2000,
  blush: 4000,
  amethyst: 4000,
  aqua: 4000,
  sage: 4000,
  hazel: 4000,
  apricot: 4000,
  denim: 4000,
  lavender: 4000,
  salmon: 7000,
  crimson: 7000,
  glacier: 7000,
  deepsea: 7000,
  iris: 7000,
  fuchsia: 7000,
  clay: 7000,
  moss: 7000,
  petal: 5000,
  powder: 5000,
  mist: 5000,
  butter: 5000,
  mintcream: 5000,
  lilac: 5000,
  pearl: 5000,
  softpeach: 5000,
  porcelain: 1000,
  fair: 1000,
  beige: 1000,
  warmbeige: 1000,
  honey: 1000,
  almond: 1000,
  umber: 1000,
  ebony: 1000,
};
const COLOR_PAGES = [[
  {
    key: 'guest',
    title: 'Basic',
    subtitle: 'Available to everyone',
    colors: [
      { id: 'ink', hex: '#111827', label: 'Ink', tier: 'guest' },
      { id: 'slate', hex: '#6B7280', label: 'Slate', tier: 'guest' },
      { id: 'cloud', hex: '#F3F4F6', label: 'Cloud', tier: 'guest' },
      { id: 'ember', hex: '#DC2626', label: 'Ember', tier: 'guest' },
      { id: 'gold', hex: '#F59E0B', label: 'Gold', tier: 'guest' },
      { id: 'sun', hex: '#FDE047', label: 'Sun', tier: 'guest' },
      { id: 'leaf', hex: '#22C55E', label: 'Leaf', tier: 'guest' },
      { id: 'sky', hex: '#2563EB', label: 'Sky', tier: 'guest' },
    ],
  },
  {
    key: 'member',
    title: 'Member',
    subtitle: 'Unlocked when logged in',
    colors: [
      { id: 'rose', hex: '#FB7185', label: 'Rose', tier: 'member' },
      { id: 'plum', hex: '#7C3AED', label: 'Plum', tier: 'member' },
      { id: 'teal', hex: '#14B8A6', label: 'Teal', tier: 'member' },
      { id: 'mint', hex: '#86EFAC', label: 'Mint', tier: 'member' },
      { id: 'cocoa', hex: '#92400E', label: 'Cocoa', tier: 'member' },
      { id: 'peach', hex: '#FB923C', label: 'Peach', tier: 'member' },
      { id: 'navy', hex: '#1D4ED8', label: 'Navy', tier: 'member' },
      { id: 'orchid', hex: '#C084FC', label: 'Orchid', tier: 'member' },
    ],
  },
  {
    key: 'member-c',
    title: 'Member',
    subtitle: 'Unlocked when logged in',
    colors: [
      { id: 'coral', hex: '#F97360', label: 'Coral', tier: 'member' },
      { id: 'wine', hex: '#9F1239', label: 'Wine', tier: 'member' },
      { id: 'ice', hex: '#67E8F9', label: 'Ice', tier: 'member' },
      { id: 'lagoon', hex: '#0F766E', label: 'Lagoon', tier: 'member' },
      { id: 'violet', hex: '#8B5CF6', label: 'Violet', tier: 'member' },
      { id: 'magenta', hex: '#DB2777', label: 'Magenta', tier: 'member' },
      { id: 'sand', hex: '#D6D3D1', label: 'Sand', tier: 'member' },
      { id: 'forest', hex: '#14532D', label: 'Forest', tier: 'member' },
    ],
  },
], [
  {
    key: 'premium-a',
    title: 'Premium',
    subtitle: 'Unlocked with Pixel Drops',
    colors: [
      { id: 'charcoal', hex: '#374151', label: 'Charcoal', tier: 'premium', paletteKey: 'mono' },
      { id: 'silver', hex: '#D1D5DB', label: 'Silver', tier: 'premium', paletteKey: 'mono' },
      { id: 'ivory', hex: '#FFF7D6', label: 'Ivory', tier: 'premium', paletteKey: 'mono' },
      { id: 'brick', hex: '#991B1B', label: 'Brick', tier: 'premium', paletteKey: 'ember' },
      { id: 'amber', hex: '#B45309', label: 'Amber', tier: 'premium', paletteKey: 'ember' },
      { id: 'lemon', hex: '#FFF176', label: 'Lemon', tier: 'premium', paletteKey: 'ember' },
      { id: 'lime', hex: '#65A30D', label: 'Lime', tier: 'premium', paletteKey: 'ember' },
      { id: 'royal', hex: '#1E40AF', label: 'Royal', tier: 'premium', paletteKey: 'ember' },
    ],
  },
  {
    key: 'premium-b',
    title: 'Premium',
    subtitle: 'Unlocked with Pixel Drops',
    colors: [
      { id: 'blush', hex: '#FBCFE8', label: 'Blush', tier: 'premium', paletteKey: 'pastel' },
      { id: 'amethyst', hex: '#6D28D9', label: 'Amethyst', tier: 'premium', paletteKey: 'pastel' },
      { id: 'aqua', hex: '#0F766E', label: 'Aqua', tier: 'premium', paletteKey: 'pastel' },
      { id: 'sage', hex: '#4ADE80', label: 'Sage', tier: 'premium', paletteKey: 'pastel' },
      { id: 'hazel', hex: '#78350F', label: 'Hazel', tier: 'premium', paletteKey: 'earth' },
      { id: 'apricot', hex: '#FED7AA', label: 'Apricot', tier: 'premium', paletteKey: 'earth' },
      { id: 'denim', hex: '#60A5FA', label: 'Denim', tier: 'premium', paletteKey: 'earth' },
      { id: 'lavender', hex: '#E9D5FF', label: 'Lavender', tier: 'premium', paletteKey: 'earth' },
    ],
  },
  {
    key: 'premium-c',
    title: 'Premium',
    subtitle: 'Unlocked with Pixel Drops',
    colors: [
      { id: 'salmon', hex: '#FDB4AF', label: 'Salmon', tier: 'premium', paletteKey: 'dawn' },
      { id: 'crimson', hex: '#7F1D1D', label: 'Crimson', tier: 'premium', paletteKey: 'dawn' },
      { id: 'glacier', hex: '#CFFAFE', label: 'Glacier', tier: 'premium', paletteKey: 'tide' },
      { id: 'deepsea', hex: '#164E63', label: 'Deep Sea', tier: 'premium', paletteKey: 'tide' },
      { id: 'iris', hex: '#A78BFA', label: 'Iris', tier: 'premium', paletteKey: 'arcade' },
      { id: 'fuchsia', hex: '#F0ABFC', label: 'Fuchsia', tier: 'premium', paletteKey: 'arcade' },
      { id: 'clay', hex: '#B45309', label: 'Clay', tier: 'premium', paletteKey: 'terra' },
      { id: 'moss', hex: '#4D7C0F', label: 'Moss', tier: 'premium', paletteKey: 'terra' },
    ],
  },
], [
  {
    key: 'skin-guest',
    title: 'Skin Tones',
    subtitle: 'Unlocked with Pixel Drops',
    colors: [
      { id: 'porcelain', hex: '#F7E3D4', label: 'Porcelain', tier: 'premium', paletteKey: 'skintones' },
      { id: 'fair', hex: '#EBCBB2', label: 'Fair', tier: 'premium', paletteKey: 'skintones' },
      { id: 'beige', hex: '#DDB193', label: 'Beige', tier: 'premium', paletteKey: 'skintones' },
      { id: 'warmbeige', hex: '#C8916B', label: 'Warm Beige', tier: 'premium', paletteKey: 'skintones' },
      { id: 'honey', hex: '#B97B52', label: 'Honey', tier: 'premium', paletteKey: 'skintones' },
      { id: 'almond', hex: '#9A6648', label: 'Almond', tier: 'premium', paletteKey: 'skintones' },
      { id: 'umber', hex: '#72462F', label: 'Umber', tier: 'premium', paletteKey: 'skintones' },
      { id: 'ebony', hex: '#4B2D20', label: 'Ebony', tier: 'premium', paletteKey: 'skintones' },
    ],
  },
  {
    key: 'pastel-member',
    title: 'Pastels',
    subtitle: 'Unlocked with Pixel Drops',
    colors: [
      { id: 'petal', hex: '#FBCFE8', label: 'Petal', tier: 'premium', paletteKey: 'pasteluxe' },
      { id: 'powder', hex: '#BFDBFE', label: 'Powder', tier: 'premium', paletteKey: 'pasteluxe' },
      { id: 'mist', hex: '#E5E7EB', label: 'Mist', tier: 'premium', paletteKey: 'pasteluxe' },
      { id: 'butter', hex: '#FEF3C7', label: 'Butter', tier: 'premium', paletteKey: 'pasteluxe' },
      { id: 'mintcream', hex: '#D1FAE5', label: 'Mint Cream', tier: 'premium', paletteKey: 'pasteluxe' },
      { id: 'lilac', hex: '#DDD6FE', label: 'Lilac', tier: 'premium', paletteKey: 'pasteluxe' },
      { id: 'pearl', hex: '#F5F3FF', label: 'Pearl', tier: 'premium', paletteKey: 'pasteluxe' },
      { id: 'softpeach', hex: '#FED7AA', label: 'Soft Peach', tier: 'premium', paletteKey: 'pasteluxe' },
    ],
  },
  {
    key: 'neon-premium',
    title: 'Neon',
    subtitle: 'Unlocked with Pixel Drops',
    colors: [
      { id: 'electricpink', hex: '#FF4FD8', label: 'Electric Pink', tier: 'premium', paletteKey: 'neon' },
      { id: 'volt', hex: '#D9FF00', label: 'Volt', tier: 'premium', paletteKey: 'neon' },
      { id: 'laser', hex: '#00E5FF', label: 'Laser', tier: 'premium', paletteKey: 'neon' },
      { id: 'acid', hex: '#7CFF6B', label: 'Acid', tier: 'premium', paletteKey: 'neon' },
      { id: 'hotorange', hex: '#FF7A00', label: 'Hot Orange', tier: 'premium', paletteKey: 'neon' },
      { id: 'ultraviolet', hex: '#8B5CFF', label: 'Ultraviolet', tier: 'premium', paletteKey: 'neon' },
      { id: 'cyberred', hex: '#FF3B5C', label: 'Cyber Red', tier: 'premium', paletteKey: 'neon' },
      { id: 'toxicteal', hex: '#00FFC2', label: 'Toxic Teal', tier: 'premium', paletteKey: 'neon' },
    ],
  },
]];
const ALL_COLORS = COLOR_PAGES.flatMap((page) => page.flatMap((group) => group.colors));

class FreeDrawCanvas extends React.Component {
  constructor(props) {
    super(props);
    this.canvasRef = React.createRef();
    this.canvasContainerRef = React.createRef();
    this.paletteScrollRef = React.createRef();

    this.scale = 1;
    this.minScale = 0.1;
    this.maxScale = 25;
    this.offsetX = 0;
    this.offsetY = 0;

    this.isMouseDown = false;
    this.downStartedHere = false;
    this.dragging = false;
    this.dragStart = { x: 0, y: 0 };
    this.paletteDragStartX = 0;
    this.paletteScrollStartLeft = 0;
    this.isPaletteDragging = false;
    this.DRAG_THRESHOLD = 3;
    this.viewportDirty = false;
    this.placementRequestInFlight = false;
    this.statusRefreshSeq = 0;
    this.appliedStatusSeq = 0;
    this.lastTouchAt = 0;

    this.offscreenCanvas = document.createElement('canvas');

    this.state = {
      mouseX: null,
      mouseY: null,
      isFullScreen: false,
      selectedPixel: null,
      hoverPixel: null,
      placementPulse: null,
      availablePixels: GUEST_FREE_DRAW_MAX_PIXELS,
      maxPixels: GUEST_FREE_DRAW_MAX_PIXELS,
      missingPixels: 0,
      msUntilNextPixel: 0,
      placingPixel: false,
      quickPixelError: null,
      lockedColorModalOpen: false,
      premiumColorModalOpen: false,
      premiumColorPurchaseLoading: false,
      premiumColorPurchaseError: null,
      pendingPremiumColor: null,
      palettePageIndex: 0,
      paletteAnimating: false,
      palettePreviousIndex: null,
      tokenActionLoading: false,
      tokenActionError: null,
      tokenLoginModalOpen: false,
      tokenUseModalOpen: false,
      tokenUseCount: 1,
      storePromptOpen: false,
      storePromptTitle: '',
      storePromptMessage: '',
    };

    this.draw = this.draw.bind(this);
  }

  initFromBackend = () => {
    const { chunks, canvas: backendCanvas } = this.props.backendContext || {};
    const canvasObject = backendCanvas?.page === this.props.page ? backendCanvas : null;

    this.virtualWidth = Math.max(1, Number(canvasObject?.dimensionX ?? 512));
    this.virtualHeight = Math.max(1, Number(canvasObject?.dimensionY ?? 512));

    this.offscreenCanvas.width = this.virtualWidth;
    this.offscreenCanvas.height = this.virtualHeight;
    this.offCtx = this.offscreenCanvas.getContext('2d', { willReadFrequently: true });
    this.offCtx.imageSmoothingEnabled = false;
    this.offCtx.fillStyle = 'white';
    this.offCtx.fillRect(0, 0, this.virtualWidth, this.virtualHeight);

    if (chunks?.length) {
      this.drawChunksToCanvas(chunks);
    }

    this.viewportDirty = false;
    this.fitCanvasToViewport();

    this.setState({
      selectedPixel: null,
      quickPixelError: null,
    }, () => {
      this.ensureSelectedColorUnlocked();
      this.draw();
    });
  };

  drawChunksToCanvas = (chunks) => {
    chunks.forEach((chunk) => {
      const { pixels } = chunk;
      const [chunkX, chunkY] = String(chunk.chunkId).split("#").map((n) => parseInt(n, 10));
      if (!pixels) return;

      const startX = chunkX * CHUNK;
      const startY = chunkY * CHUNK;
      const binary = atob(chunk.pixels);
      const bytes = new Uint8ClampedArray(binary.length);

      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }

      const imageData = new ImageData(bytes, CHUNK, CHUNK);
      this.offCtx.putImageData(imageData, startX, startY);
    });
  };

  applyChunkRecordToOffscreen = (chunk) => {
    if (!chunk?.pixels || !chunk?.chunkId || !this.offCtx) return;

    const [chunkX, chunkY] = String(chunk.chunkId).split("#").map((n) => parseInt(n, 10));
    if (!Number.isFinite(chunkX) || !Number.isFinite(chunkY)) return;

    const startX = chunkX * CHUNK;
    const startY = chunkY * CHUNK;
    const binary = atob(chunk.pixels);
    const bytes = new Uint8ClampedArray(binary.length);

    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    const imageData = new ImageData(bytes, CHUNK, CHUNK);
    this.offCtx.putImageData(imageData, startX, startY);
  };

  bindCanvasListeners = () => {
    const canvas = this.canvasRef.current;
    if (!canvas || this._canvasBound === canvas) return;

    if (this._canvasBound && this._canvasBound !== canvas) {
      try {
        this._canvasBound.removeEventListener('mousedown', this.onMouseDown);
        this._canvasBound.removeEventListener('mouseup', this.onMouseUp);
        this._canvasBound.removeEventListener('mouseleave', this.onMouseUp);
        this._canvasBound.removeEventListener('mousemove', this.onMouseMove);
        this._canvasBound.removeEventListener('touchstart', this.onTouchStart);
        this._canvasBound.removeEventListener('touchmove', this.onTouchMove);
        this._canvasBound.removeEventListener('touchend', this.onTouchEnd);
        this._canvasBound.removeEventListener('touchcancel', this.onTouchCancel);
        this._canvasBound.removeEventListener('wheel', this.onWheel);
        this._canvasBound.removeEventListener('contextmenu', this.onContextMenu);
        window.removeEventListener('resize', this.resizeCanvas);
      } catch {}
    }

    canvas.addEventListener('mousedown', this.onMouseDown);
    canvas.addEventListener('mouseup', this.onMouseUp);
    canvas.addEventListener('mouseleave', this.onMouseUp);
    canvas.addEventListener('mousemove', this.onMouseMove);
    canvas.addEventListener('touchstart', this.onTouchStart, { passive: true });
    canvas.addEventListener('touchmove', this.onTouchMove, { passive: false });
    canvas.addEventListener('touchend', this.onTouchEnd, { passive: true });
    canvas.addEventListener('touchcancel', this.onTouchCancel, { passive: true });
    canvas.addEventListener('wheel', this.onWheel, { passive: false });
    canvas.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('resize', this.resizeCanvas);

    this._canvasBound = canvas;
    this.resizeCanvas();
  };

  async componentDidMount() {
    const { backendContext } = this.props;
    this.initFromBackend();

    this.unsubscribeChunkSubscription = await backendContext.subscribeToFreeCanvasChunkUpdates?.({ page: this.props.page });
    this.unsubscribeCanvasUpdatesLocal = backendContext.subscribeToCanvasUpdatesLocal(async (evt) => {
      try {
        const kind = evt?.kind;
        const rec = evt?.record ?? evt;
        if (kind !== 'FreeCanvasChunk' || !rec || rec.page !== this.props.page) return;

        this.applyChunkRecordToOffscreen(rec);
        this.draw();
      } catch (error) {
        console.error('free draw subscriber error:', error);
      }
    });

    const canvas = this.canvasRef.current;
    if (!canvas) return;

    window.addEventListener('resize', this.resizeCanvas);
    this.resizeCanvas();

    canvas.addEventListener('mousedown', this.onMouseDown);
    canvas.addEventListener('mouseup', this.onMouseUp);
    canvas.addEventListener('mouseleave', this.onMouseUp);
    canvas.addEventListener('mousemove', this.onMouseMove);
    canvas.addEventListener('touchstart', this.onTouchStart, { passive: true });
    canvas.addEventListener('touchmove', this.onTouchMove, { passive: false });
    canvas.addEventListener('touchend', this.onTouchEnd, { passive: true });
    canvas.addEventListener('touchcancel', this.onTouchCancel, { passive: true });
    canvas.addEventListener('wheel', this.onWheel, { passive: false });
    canvas.addEventListener('contextmenu', this.onContextMenu);

    this.draw();
    this.ensureSelectedColorUnlocked();
    await this.refreshFreeDrawStatus();
  }

  componentWillUnmount() {
    try { this.unsubscribeChunkSubscription?.(); } catch {}
    try { this.unsubscribeCanvasUpdatesLocal?.(); } catch {}
    const canvas = this.canvasRef.current;
    if (canvas) {
      canvas.removeEventListener('mousedown', this.onMouseDown);
      canvas.removeEventListener('mouseup', this.onMouseUp);
      canvas.removeEventListener('mouseleave', this.onMouseUp);
      canvas.removeEventListener('mousemove', this.onMouseMove);
      canvas.removeEventListener('touchstart', this.onTouchStart);
      canvas.removeEventListener('touchmove', this.onTouchMove);
      canvas.removeEventListener('touchend', this.onTouchEnd);
      canvas.removeEventListener('touchcancel', this.onTouchCancel);
      canvas.removeEventListener('wheel', this.onWheel);
      canvas.removeEventListener('contextmenu', this.onContextMenu);
    }

    window.removeEventListener('resize', this.resizeCanvas);
    window.removeEventListener('mousemove', this.onPaletteMouseMove);
    window.removeEventListener('mouseup', this.onPaletteMouseUp);

    clearInterval(this.cooldownInterval);
    clearTimeout(this.placementPulseTimeout);
    clearTimeout(this.paletteAnimationTimeout);
  }

  componentDidUpdate(prevProps, prevState) {
    const prevCanvas = prevProps.backendContext.canvas;
    const nextCanvas = this.props.backendContext.canvas;
    const canvasChanged = prevCanvas !== nextCanvas;
    const pageChanged = prevProps.page !== this.props.page;

    if (prevProps.backendContext.chunks !== this.props.backendContext.chunks || canvasChanged) {
      this.initFromBackend();
      this.bindCanvasListeners();
    }

    if (
      prevProps.selectedColor !== this.props.selectedColor ||
      prevProps.backendContext?.userProfile !== this.props.backendContext?.userProfile
    ) {
      this.ensureSelectedColorUnlocked();
    }

    if (
      pageChanged ||
      prevProps.backendContext?.userProfile?.sub !== this.props.backendContext?.userProfile?.sub
    ) {
      this.refreshFreeDrawStatus();
    }

    if (pageChanged) {
      try { this.unsubscribeChunkSubscription?.(); } catch {}
      Promise.resolve(this.props.backendContext.subscribeToFreeCanvasChunkUpdates?.({ page: this.props.page }))
        .then((unsubscribe) => {
          this.unsubscribeChunkSubscription = unsubscribe;
        })
        .catch((error) => {
          console.error('Failed to resubscribe to free draw chunks:', error);
        });
    }

    if (
      prevProps.selectedColor !== this.props.selectedColor ||
      prevState.hoverPixel !== this.state.hoverPixel ||
      prevState.placementPulse !== this.state.placementPulse
    ) {
      this.draw();
    }
  }

  clampOffsets = (nextOffsetX, nextOffsetY, scale = this.scale) => {
    const canvas = this.canvasRef.current;
    if (!canvas) {
      return { offsetX: nextOffsetX, offsetY: nextOffsetY };
    }

    const visibleCanvasWidth = canvas.width / scale;
    const visibleCanvasHeight = canvas.height / scale;

    const minOffsetX = visibleCanvasWidth >= this.virtualWidth
      ? (this.virtualWidth - visibleCanvasWidth) / 2
      : 0;
    const maxOffsetX = visibleCanvasWidth >= this.virtualWidth
      ? minOffsetX
      : this.virtualWidth - visibleCanvasWidth;

    const minOffsetY = visibleCanvasHeight >= this.virtualHeight
      ? (this.virtualHeight - visibleCanvasHeight) / 2
      : 0;
    const maxOffsetY = visibleCanvasHeight >= this.virtualHeight
      ? minOffsetY
      : this.virtualHeight - visibleCanvasHeight;

    return {
      offsetX: Math.min(Math.max(nextOffsetX, minOffsetX), maxOffsetX),
      offsetY: Math.min(Math.max(nextOffsetY, minOffsetY), maxOffsetY),
    };
  };

  fitCanvasToViewport = () => {
    const canvas = this.canvasRef.current;
    if (!canvas || !this.virtualWidth || !this.virtualHeight) return;

    const visibleWidth = canvas.width;
    const visibleHeight = canvas.height;
    if (!visibleWidth || !visibleHeight) return;

    const fitScale = Math.max(visibleWidth / this.virtualWidth, visibleHeight / this.virtualHeight);
    this.scale = Math.min(this.maxScale, Math.max(this.minScale, fitScale));

    const visibleCanvasWidth = visibleWidth / this.scale;
    const visibleCanvasHeight = visibleHeight / this.scale;
    const centeredOffsets = this.clampOffsets(
      (this.virtualWidth - visibleCanvasWidth) / 2,
      (this.virtualHeight - visibleCanvasHeight) / 2,
      this.scale
    );
    this.offsetX = centeredOffsets.offsetX;
    this.offsetY = centeredOffsets.offsetY;
  };

  resizeCanvas = () => {
    const canvas = this.canvasRef.current;
    const container = this.canvasContainerRef.current;
    if (canvas && container) {
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      if (!this.viewportDirty) {
        this.fitCanvasToViewport();
      }
      this.draw();
    }
  };

  getMouseCanvasPixel(e) {
    const canvas = this.canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / this.scale + this.offsetX);
    const y = Math.floor((e.clientY - rect.top) / this.scale + this.offsetY);
    return { x, y };
  }

  getClientCanvasPixel = (clientX, clientY) => {
    const canvas = this.canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = Math.floor((clientX - rect.left) / this.scale + this.offsetX);
    const y = Math.floor((clientY - rect.top) / this.scale + this.offsetY);
    return { x, y };
  };

  drawSelectionCorners(ctx, x, y) {
    const L = 0.25;
    ctx.save();
    ctx.lineWidth = 1 / this.scale;
    ctx.strokeStyle = '#000000';
    ctx.beginPath(); ctx.moveTo(x, y + L); ctx.lineTo(x, y); ctx.lineTo(x + L, y); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x + 1 - L, y); ctx.lineTo(x + 1, y); ctx.lineTo(x + 1, y + L); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x + 1, y + 1 - L); ctx.lineTo(x + 1, y + 1); ctx.lineTo(x + 1 - L, y + 1); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x + L, y + 1); ctx.lineTo(x, y + 1); ctx.lineTo(x, y + 1 - L); ctx.stroke();
    ctx.restore();
  }

  isInsideCanvas = (e) => {
    const canvas = this.canvasRef.current;
    const r = canvas.getBoundingClientRect();
    return e.clientX >= r.left && e.clientX < r.right && e.clientY >= r.top && e.clientY < r.bottom;
  };

  isInsideClientPoint = (clientX, clientY) => {
    const canvas = this.canvasRef.current;
    const r = canvas.getBoundingClientRect();
    return clientX >= r.left && clientX < r.right && clientY >= r.top && clientY < r.bottom;
  };

  isSyntheticMouseEvent = () => Date.now() - this.lastTouchAt < 500;

  handlePixelSelection = (x, y) => {
    this.setState({
      selectedPixel: { x, y },
      quickPixelError: null,
      tokenActionError: null,
    }, () => {
      this.draw();
      this.refreshFreeDrawStatus();
    });
  };

  onMouseDown = (e) => {
    if (this.isSyntheticMouseEvent()) return;
    if (e.button !== 0) return;
    if (!this.isInsideCanvas(e)) return;

    this.isMouseDown = true;
    this.downStartedHere = true;
    this.dragging = false;
    this.dragStart.x = e.clientX;
    this.dragStart.y = e.clientY;
  };

  onMouseUp = (e) => {
    if (this.isSyntheticMouseEvent()) return;
    if (e.button !== 0) return;
    if (!this.downStartedHere) return;

    const wasDragging = this.dragging;
    const inside = this.isInsideCanvas(e);

    this.isMouseDown = false;
    this.dragging = false;
    this.downStartedHere = false;

    if (wasDragging || !inside) return;

    const { x, y } = this.getMouseCanvasPixel(e);
    if (x < 0 || y < 0 || x >= this.virtualWidth || y >= this.virtualHeight) return;
    this.handlePixelSelection(x, y);
  };

  onMouseMove = (e) => {
    if (this.isSyntheticMouseEvent()) return;
    const inside = this.isInsideCanvas(e);
    if (inside) {
      const { x, y } = this.getMouseCanvasPixel(e);
      this.setState({
        mouseX: x >= 0 && x < this.virtualWidth ? x + 1 : this.state.mouseX,
        mouseY: y >= 0 && y < this.virtualHeight ? y + 1 : this.state.mouseY,
        hoverPixel: x >= 0 && x < this.virtualWidth && y >= 0 && y < this.virtualHeight
          ? { x, y }
          : null,
      });
    } else if (this.state.hoverPixel || this.state.mouseX !== null || this.state.mouseY !== null) {
      this.setState({
        hoverPixel: null,
        mouseX: null,
        mouseY: null,
      });
    }

    if (!this.isMouseDown) return;

    const dxClient = e.clientX - this.dragStart.x;
    const dyClient = e.clientY - this.dragStart.y;
    if (!this.dragging) {
      const moved2 = dxClient * dxClient + dyClient * dyClient;
      if (moved2 < this.DRAG_THRESHOLD * this.DRAG_THRESHOLD) return;
      this.dragging = true;
      this.viewportDirty = true;
    }

    const dx = dxClient / this.scale;
    const dy = dyClient / this.scale;

    const nextOffsets = this.clampOffsets(this.offsetX - dx, this.offsetY - dy, this.scale);
    this.offsetX = nextOffsets.offsetX;
    this.offsetY = nextOffsets.offsetY;

    this.dragStart.x = e.clientX;
    this.dragStart.y = e.clientY;
    this.draw();
  };

  onTouchStart = (e) => {
    if (!e.touches?.length) return;
    const touch = e.touches[0];
    if (!this.isInsideClientPoint(touch.clientX, touch.clientY)) return;

    this.lastTouchAt = Date.now();
    this.isMouseDown = true;
    this.downStartedHere = true;
    this.dragging = false;
    this.dragStart.x = touch.clientX;
    this.dragStart.y = touch.clientY;
  };

  onTouchMove = (e) => {
    if (!e.touches?.length) return;
    const touch = e.touches[0];
    this.lastTouchAt = Date.now();

    if (this.isInsideClientPoint(touch.clientX, touch.clientY)) {
      const { x, y } = this.getClientCanvasPixel(touch.clientX, touch.clientY);
      this.setState({
        mouseX: x >= 0 && x < this.virtualWidth ? x + 1 : this.state.mouseX,
        mouseY: y >= 0 && y < this.virtualHeight ? y + 1 : this.state.mouseY,
        hoverPixel: x >= 0 && x < this.virtualWidth && y >= 0 && y < this.virtualHeight
          ? { x, y }
          : null,
      });
    }

    if (!this.isMouseDown) return;

    e.preventDefault();
    const dxClient = touch.clientX - this.dragStart.x;
    const dyClient = touch.clientY - this.dragStart.y;
    if (!this.dragging) {
      const moved2 = dxClient * dxClient + dyClient * dyClient;
      if (moved2 < this.DRAG_THRESHOLD * this.DRAG_THRESHOLD) return;
      this.dragging = true;
      this.viewportDirty = true;
    }

    const dx = dxClient / this.scale;
    const dy = dyClient / this.scale;
    const nextOffsets = this.clampOffsets(this.offsetX - dx, this.offsetY - dy, this.scale);
    this.offsetX = nextOffsets.offsetX;
    this.offsetY = nextOffsets.offsetY;

    this.dragStart.x = touch.clientX;
    this.dragStart.y = touch.clientY;
    this.draw();
  };

  onTouchEnd = (e) => {
    this.lastTouchAt = Date.now();
    if (!this.downStartedHere) return;

    const touch = e.changedTouches?.[0];
    const wasDragging = this.dragging;
    const inside = touch ? this.isInsideClientPoint(touch.clientX, touch.clientY) : false;

    this.isMouseDown = false;
    this.dragging = false;
    this.downStartedHere = false;

    if (wasDragging || !inside || !touch) return;

    const { x, y } = this.getClientCanvasPixel(touch.clientX, touch.clientY);
    if (x < 0 || y < 0 || x >= this.virtualWidth || y >= this.virtualHeight) return;
    this.handlePixelSelection(x, y);
  };

  onTouchCancel = () => {
    this.lastTouchAt = Date.now();
    this.isMouseDown = false;
    this.dragging = false;
    this.downStartedHere = false;
  };

  onWheel = (e) => {
    e.preventDefault();
    const canvas = this.canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const zoomX = mx / this.scale + this.offsetX;
    const zoomY = my / this.scale + this.offsetY;

    this.viewportDirty = true;
    const delta = e.deltaY < 0 ? 1.2 : 0.8;
    const minVisibleScale = Math.max(canvas.width / this.virtualWidth, canvas.height / this.virtualHeight);
    const newScale = Math.min(this.maxScale, Math.max(minVisibleScale, this.scale * delta));
    this.scale = newScale;

    const nextOffsets = this.clampOffsets(zoomX - mx / newScale, zoomY - my / newScale, newScale);
    this.offsetX = nextOffsets.offsetX;
    this.offsetY = nextOffsets.offsetY;
    this.draw();
  };

  onContextMenu = (e) => e.preventDefault();

  adjustZoom = (delta) => {
    this.viewportDirty = true;
    const canvas = this.canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mx = rect.width / 2;
    const my = rect.height / 2;
    const zoomX = mx / this.scale + this.offsetX;
    const zoomY = my / this.scale + this.offsetY;
    const minVisibleScale = Math.max(canvas.width / this.virtualWidth, canvas.height / this.virtualHeight);
    const newScale = Math.min(this.maxScale, Math.max(minVisibleScale, this.scale * delta));
    this.scale = newScale;
    const nextOffsets = this.clampOffsets(zoomX - mx / newScale, zoomY - my / newScale, newScale);
    this.offsetX = nextOffsets.offsetX;
    this.offsetY = nextOffsets.offsetY;
    this.draw();
  };

  toggleFullScreen = () => {
    this.viewportDirty = false;
    this.setState((prev) => ({ isFullScreen: !prev.isFullScreen }), this.resizeCanvas);
  };

  startRestoreTicker = (nextRestoreAt) => {
    clearInterval(this.cooldownInterval);
    const target = Date.parse(nextRestoreAt || '');
    if (!Number.isFinite(target)) {
      this.setState({ msUntilNextPixel: 0 });
      return;
    }

    const tick = () => {
      const remaining = Math.max(0, target - Date.now());
      if (remaining <= 0) {
        clearInterval(this.cooldownInterval);
        this.setState({ msUntilNextPixel: 0 }, () => {
          this.refreshFreeDrawStatus();
        });
        return;
      }
      this.setState({ msUntilNextPixel: remaining });
    };

    tick();
    this.cooldownInterval = setInterval(tick, 1000);
  };

  formatCooldown = (ms) => {
    const totalSec = Math.max(0, Math.ceil(ms / 1000));
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  getRestoreStatusLabel = (remainingMs) => {
    if (remainingMs <= 10000) return 'Restoring now';
    if (remainingMs <= 30000) return 'Pixel almost back';
    if (remainingMs <= 60000) return 'Next pixel soon';
    return 'Recharging';
  };

  triggerPlacementPulse = (x, y) => {
    clearTimeout(this.placementPulseTimeout);
    this.setState({ placementPulse: { x, y, startedAt: Date.now() } }, () => {
      this.draw();
    });
    this.placementPulseTimeout = setTimeout(() => {
      this.setState({ placementPulse: null }, () => this.draw());
    }, 350);
  };

  applyFreeDrawStatus = (status, fallbackError = null, seq = null) => {
    if (!status) return;
    if (seq !== null && seq < this.appliedStatusSeq) return;
    if (seq !== null) {
      this.appliedStatusSeq = seq;
    }

    if (!status?.nextRestoreAt) {
      clearInterval(this.cooldownInterval);
    }

    const fallbackMaxPixels = this.props.backendContext?.userProfile?.sub
      ? BASE_FREE_DRAW_MAX_PIXELS
      : GUEST_FREE_DRAW_MAX_PIXELS;

    this.setState({
      availablePixels: Math.max(0, Number(status.availablePixels ?? fallbackMaxPixels)),
      maxPixels: Math.max(1, Number(status.maxPixels ?? fallbackMaxPixels)),
      missingPixels: Math.max(0, Number(status.missingPixels ?? 0)),
      msUntilNextPixel: Math.max(0, Number(status.msUntilNextPixel ?? 0)),
      tokenActionError: status?.ok === false ? (fallbackError || 'Unable to refresh free draw status right now.') : fallbackError,
    });

    if (status?.nextRestoreAt) {
      this.startRestoreTicker(status.nextRestoreAt);
    }
  };

  refreshFreeDrawStatus = async () => {
    const seq = ++this.statusRefreshSeq;
    const status = await this.props.backendContext.getFreeDrawStatus?.();
    this.applyFreeDrawStatus(status, null, seq);
  };

  getUnlockedPaletteKeys = () => {
    const owned = this.props.backendContext?.userProfile?.ownedPalettes;
    if (!Array.isArray(owned)) {
      return new Set();
    }
    return new Set(owned.map((entry) => String(entry)));
  };

  isLoggedIn = () => !!this.props.backendContext?.userProfile?.sub;

  getPlaceTokenCount = () => Math.max(0, Number(this.props.backendContext?.userProfile?.placeTokens ?? 0));

  isColorUnlocked = (color) => {
    if (color.tier === 'guest') return true;
    if (color.tier === 'member') return this.isLoggedIn();
    const ownedPaletteKeys = this.getUnlockedPaletteKeys();
    return ownedPaletteKeys.has(color.paletteKey) || ownedPaletteKeys.has(color.id);
  };

  getSelectedColorMeta = () => {
    const { selectedColor } = this.props;
    const normalizedSelectedColor = String(selectedColor || '').toLowerCase();
    const match = ALL_COLORS.find((color) => color.hex.toLowerCase() === normalizedSelectedColor);
    return match || ALL_COLORS[0];
  };

  getFirstUnlockedColor = () => {
    const match = ALL_COLORS.find((color) => this.isColorUnlocked(color));
    return match || ALL_COLORS[0];
  };

  ensureSelectedColorUnlocked = () => {
    const selectedMeta = this.getSelectedColorMeta();
    if (this.isColorUnlocked(selectedMeta) && String(this.props.selectedColor || '').toLowerCase() === selectedMeta.hex.toLowerCase()) {
      return;
    }

    const fallback = this.getFirstUnlockedColor();
    if (fallback?.hex && fallback.hex !== this.props.selectedColor) {
      this.props.setSelectedColor(fallback.hex);
    }
  };

  handlePaletteColorClick = (color) => {
    if (!this.isColorUnlocked(color)) {
      if (!this.isLoggedIn()) {
        this.setState({ lockedColorModalOpen: true });
        return;
      }

      if (color.tier === 'premium') {
        this.setState({
          premiumColorModalOpen: true,
          pendingPremiumColor: color,
          premiumColorPurchaseError: null,
        });
        return;
      }

      return;
    }
    this.props.setSelectedColor(color.hex);
  };

  getLockedMessage = () => {
    if (!this.isLoggedIn()) {
      return 'Log in to unlock more free draw colors.';
    }
    return 'This premium color can be unlocked with Pixel Drops.';
  };

  getPremiumColorCost = (color) => (
    PREMIUM_COLOR_COSTS[String(color?.id || '').toLowerCase()] ?? 5000
  );

  handleBuyPremiumColor = async () => {
    const color = this.state.pendingPremiumColor;
    if (!color?.id) return;

    try {
      this.setState({
        premiumColorPurchaseLoading: true,
        premiumColorPurchaseError: null,
      });

      const result = await this.props.backendContext.purchasePremiumColor?.(color.id);

      if (result === 'success' || result === 'already-owned') {
        this.props.setSelectedColor(color.hex);
        this.setState({
          premiumColorModalOpen: false,
          pendingPremiumColor: null,
          premiumColorPurchaseError: null,
        });
        return;
      }

      if (String(result || '').toLowerCase().includes('insufficient tokens')) {
        this.setState({
          premiumColorModalOpen: false,
          pendingPremiumColor: null,
          premiumColorPurchaseError: null,
        });
        this.openStorePrompt(
          'Not Enough Pixel Drops',
          'You do not have enough Pixel Drops to unlock this premium color.'
        );
        return;
      }

      this.setState({
        premiumColorPurchaseError: String(result || 'Unable to unlock this premium color right now.'),
      });
    } catch (error) {
      console.error(error);
      this.setState({
        premiumColorPurchaseError: 'Unable to unlock this premium color right now.',
      });
    } finally {
      this.setState({ premiumColorPurchaseLoading: false });
    }
  };

  requireTokenLogin = () => {
    if (this.isLoggedIn()) return true;
    this.setState({ tokenLoginModalOpen: true });
    return false;
  };

  openStorePrompt = (title, message) => {
    this.setState({
      storePromptOpen: true,
      storePromptTitle: title,
      storePromptMessage: message,
    });
  };

  openUseTokenModal = () => {
    if (!this.requireTokenLogin()) return;
    if (this.getPlaceTokenCount() <= 0) {
      this.openStorePrompt(
        'No Restore Tokens',
        'Restore Tokens refill missing free-draw pixels. You are out of tokens right now.'
      );
      return;
    }
    if (this.state.missingPixels <= 0) {
      this.setState({ tokenActionError: 'Your pixel meter is already full.' });
      return;
    }
    this.setState({
      tokenUseModalOpen: true,
      tokenUseCount: Math.min(this.state.missingPixels, 1),
      tokenActionError: null,
      quickPixelError: null,
    });
  };

  handleUsePlaceTokens = async () => {
    if (!this.requireTokenLogin()) return;
    if (this.state.tokenUseCount > this.getPlaceTokenCount()) {
      this.setState({ tokenUseModalOpen: false });
      this.openStorePrompt(
        'Not Enough Restore Tokens',
        'You do not have enough Restore Tokens for that refill. Buy more tokens in the store to restore more pixels at once.'
      );
      return;
    }

    try {
      this.setState({ tokenActionLoading: true, tokenActionError: null });
      const result = await this.props.backendContext.usePlaceTokensForFreeDraw?.(this.state.tokenUseCount);
      if (!result?.ok) {
        if (String(result?.message || '').toLowerCase().includes('no_place_tokens')) {
          this.setState({ tokenUseModalOpen: false });
          this.openStorePrompt(
            'No Restore Tokens',
            'You are out of Restore Tokens right now.'
          );
        } else {
          this.applyFreeDrawStatus(result, 'Unable to restore free draw pixels right now.');
        }
        return;
      }
      this.applyFreeDrawStatus(result);
      this.setState({ tokenUseModalOpen: false });
    } catch (error) {
      console.error(error);
      this.setState({ tokenActionError: 'Unable to restore free draw pixels right now.' });
    } finally {
      this.setState({ tokenActionLoading: false });
    }
  };

  renderPaletteSwatch = (color) => {
    const { selectedColor } = this.props;
    const unlocked = this.isColorUnlocked(color);
    const isSelected = String(selectedColor || '').toLowerCase() === color.hex.toLowerCase();

    return (
      <button
        key={color.id}
        type="button"
        onClick={() => this.handlePaletteColorClick(color)}
        className={`relative h-8 w-8 shrink-0 rounded-lg border transition-all ${
          isSelected
            ? 'border-white ring-2 ring-white/70 ring-offset-1 ring-offset-slate-950'
            : 'border-slate-700 hover:border-slate-400'
        } ${!unlocked ? 'cursor-pointer' : ''}`}
        style={{ backgroundColor: color.hex }}
        aria-pressed={isSelected}
        title={unlocked ? color.label : this.getLockedMessage()}
      >
        {!unlocked && (
          <span className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-slate-950/90 text-white shadow-sm">
            <Lock size={10} />
          </span>
        )}
      </button>
    );
  };

  renderPalettePage = (pageIndex) => {
    const paletteGroups = COLOR_PAGES[pageIndex] ?? COLOR_PAGES[0];

    return (
      <div className="flex items-center gap-5 min-w-max rounded-2xl border border-slate-800/70 bg-slate-900/90 px-2.5 py-2 shadow-[0_10px_22px_rgba(2,6,23,0.24)]">
        {paletteGroups.map((group) => (
          <div key={group.key} className="flex items-center gap-2 min-w-max">
            {group.colors.map(this.renderPaletteSwatch)}
          </div>
        ))}
      </div>
    );
  };

  getPaletteLayerClasses = (pageIndex) => {
    const { palettePageIndex, paletteAnimating, palettePreviousIndex } = this.state;
    const isActive = pageIndex === palettePageIndex;
    const isPrevious = pageIndex === palettePreviousIndex;

    if (paletteAnimating) {
      if (isActive) return 'translate-y-0 scale-100 rotate-0 opacity-100 z-20 shadow-[0_14px_28px_rgba(2,6,23,0.32)]';
      if (isPrevious) return 'translate-y-4 scale-[0.975] -rotate-[2deg] opacity-52 z-10 shadow-[0_8px_18px_rgba(2,6,23,0.16)]';
    }

    return isActive
      ? 'translate-y-0 scale-100 rotate-0 opacity-100 z-20 shadow-[0_14px_28px_rgba(2,6,23,0.3)]'
      : 'translate-y-4 scale-[0.975] -rotate-[2deg] opacity-52 z-10 shadow-[0_8px_18px_rgba(2,6,23,0.16)]';
  };

  renderPaletteDeck = () => (
    <div
      className="inline-grid min-w-max pr-2"
      style={{ perspective: '1200px', transformStyle: 'preserve-3d' }}
    >
      {COLOR_PAGES.map((_, pageIndex) => (
        <div
          key={`palette-page-${pageIndex}`}
          className={`col-start-1 row-start-1 origin-top-left transition-all duration-200 ease-out ${this.getPaletteLayerClasses(pageIndex)}`}
          style={{ transformStyle: 'preserve-3d' }}
        >
          {this.renderPalettePage(pageIndex)}
        </div>
      ))}
    </div>
  );

  cyclePalettePage = () => {
    if (this.state.paletteAnimating) return;

    this.setState((prev) => ({
      palettePreviousIndex: prev.palettePageIndex,
      palettePageIndex: (prev.palettePageIndex + 1) % COLOR_PAGES.length,
      paletteAnimating: true,
    }), () => {
      if (this.paletteScrollRef.current) {
        this.paletteScrollRef.current.scrollLeft = 0;
      }

      clearTimeout(this.paletteAnimationTimeout);
      this.paletteAnimationTimeout = setTimeout(() => {
        this.setState({
          paletteAnimating: false,
          palettePreviousIndex: null,
        });
      }, 200);
    });
  };

  onPaletteMouseDown = (e) => {
    const scroller = this.paletteScrollRef.current;
    if (!scroller) return;

    this.isPaletteDragging = true;
    this.paletteDragStartX = e.clientX;
    this.paletteScrollStartLeft = scroller.scrollLeft;
    window.addEventListener('mousemove', this.onPaletteMouseMove);
    window.addEventListener('mouseup', this.onPaletteMouseUp);
  };

  onPaletteMouseMove = (e) => {
    const scroller = this.paletteScrollRef.current;
    if (!this.isPaletteDragging || !scroller) return;

    const deltaX = e.clientX - this.paletteDragStartX;
    scroller.scrollLeft = this.paletteScrollStartLeft - deltaX;
  };

  onPaletteMouseUp = () => {
    this.isPaletteDragging = false;
    window.removeEventListener('mousemove', this.onPaletteMouseMove);
    window.removeEventListener('mouseup', this.onPaletteMouseUp);
  };

  placePixel = async () => {
    const { selectedPixel, availablePixels } = this.state;
    const { backendContext, selectedColor, page } = this.props;
    const selectedMeta = this.getSelectedColorMeta();

    if (this.placementRequestInFlight) return;
    if (!selectedPixel) return;
    if (availablePixels <= 0) {
      this.openUseTokenModal();
      return;
    }
    if (!this.isColorUnlocked(selectedMeta)) {
      this.setState({ quickPixelError: this.getLockedMessage() });
      return;
    }

    let previousPixel = null;

    try {
      this.placementRequestInFlight = true;
      this.setState({ placingPixel: true, quickPixelError: null });

      previousPixel = this.offCtx?.getImageData(selectedPixel.x, selectedPixel.y, 1, 1);
      this.offCtx.fillStyle = selectedColor;
      this.offCtx.fillRect(selectedPixel.x, selectedPixel.y, 1, 1);
      this.draw();

      const result = await backendContext.placeFreePixel({
        page,
        row: selectedPixel.y + 1,
        column: selectedPixel.x + 1,
        color: selectedColor,
      });

      if (!result?.ok) {
        if (previousPixel) {
          this.offCtx.putImageData(previousPixel, selectedPixel.x, selectedPixel.y);
          this.draw();
        }
        this.setState({
          quickPixelError: result?.reason === 'NO_PIXELS_AVAILABLE'
            ? 'You are out of available pixels. Wait for a refill or use a Restore Token.'
            : 'Unable to place pixel right now.',
        });
        this.applyFreeDrawStatus(result);
        return;
      }

      this.setState({
        quickPixelError: null,
      });
      this.appliedStatusSeq = ++this.statusRefreshSeq;
      this.applyFreeDrawStatus(result, null, this.appliedStatusSeq);
      this.triggerPlacementPulse(selectedPixel.x, selectedPixel.y);
    } catch (err) {
      console.error(err);
      if (previousPixel) {
        this.offCtx.putImageData(previousPixel, selectedPixel.x, selectedPixel.y);
        this.draw();
      }
      this.setState({ quickPixelError: 'Unable to place pixel right now.' });
    } finally {
      this.placementRequestInFlight = false;
      this.setState({ placingPixel: false });
    }
  };

  draw = () => {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.setTransform(this.scale, 0, 0, this.scale, -this.offsetX * this.scale, -this.offsetY * this.scale);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(this.offscreenCanvas, 0, 0);

    const hoveredPixel = this.state.hoverPixel;
    const previewPixel = this.state.selectedPixel ?? hoveredPixel;
    const selectedMeta = this.getSelectedColorMeta();
    if (previewPixel && !this.dragging) {
      ctx.save();
      ctx.fillStyle = `${selectedMeta.hex}66`;
      ctx.fillRect(previewPixel.x, previewPixel.y, 1, 1);
      ctx.lineWidth = 1 / this.scale;
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.strokeRect(previewPixel.x, previewPixel.y, 1, 1);
      ctx.restore();
    }

    const sp = this.state.selectedPixel;
    if (sp) {
      this.drawSelectionCorners(ctx, sp.x, sp.y);
    }

    const pulse = this.state.placementPulse;
    if (pulse) {
      const elapsed = Date.now() - pulse.startedAt;
      const progress = Math.min(1, elapsed / 350);
      const inset = progress * 0.35;
      ctx.save();
      ctx.lineWidth = Math.max(1 / this.scale, (1.75 - progress) / this.scale);
      ctx.strokeStyle = `rgba(250, 204, 21, ${0.95 - (progress * 0.85)})`;
      ctx.strokeRect(pulse.x - inset, pulse.y - inset, 1 + (inset * 2), 1 + (inset * 2));
      ctx.restore();
    }
  };

  renderPlacePanel() {
    const {
      selectedPixel,
      availablePixels,
      maxPixels,
      missingPixels,
      msUntilNextPixel,
      placingPixel,
      quickPixelError,
      lockedColorModalOpen,
      premiumColorModalOpen,
      premiumColorPurchaseLoading,
      premiumColorPurchaseError,
      pendingPremiumColor,
      tokenActionLoading,
      tokenActionError,
      tokenLoginModalOpen,
      tokenUseModalOpen,
      tokenUseCount,
      palettePageIndex,
      paletteAnimating,
      storePromptOpen,
      storePromptTitle,
      storePromptMessage,
    } = this.state;

    if (!selectedPixel) return null;

    const selectedMeta = this.getSelectedColorMeta();
    const selectedUnlocked = this.isColorUnlocked(selectedMeta);
    const helperText = !selectedUnlocked ? this.getLockedMessage() : null;
    const placeTokenCount = this.getPlaceTokenCount();
    const canPlaceWithCurrentState = !placingPixel;
    const maxTokenSelectable = Math.max(1, missingPixels);
    const selectedTokenUseCount = Math.min(tokenUseCount, maxTokenSelectable);

    return (
      <div className="fixed inset-x-0 bottom-3 z-[900] pointer-events-none px-3">
        <div className="pointer-events-auto mx-auto w-[min(96vw,1180px)] rounded-2xl border border-slate-700 bg-slate-950/95 shadow-2xl backdrop-blur">
          <div className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={() => this.setState({ selectedPixel: null, quickPixelError: null })}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-red-600/20 hover:bg-red-600 text-red-400 transition-all"
                aria-label="Close selection"
              >
                <X size={16} />
              </button>

              <div className="text-sm text-gray-300 whitespace-nowrap">
                ({selectedPixel.x + 1}, {selectedPixel.y + 1})
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0 rounded-xl border border-slate-800 bg-slate-900/80 px-2.5 py-1.5">
              <div
                className="h-6 w-6 rounded-md border border-white/15"
                style={{ backgroundColor: selectedMeta.hex }}
              />
              <div className="leading-tight">
                <div className="text-xs font-semibold text-white">{selectedMeta.label}</div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">
                  {selectedMeta.tier}
                </div>
              </div>
            </div>

            <div
              ref={this.paletteScrollRef}
              onMouseDown={this.onPaletteMouseDown}
              className={`min-w-0 flex-1 overflow-x-auto overflow-y-hidden select-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${this.isPaletteDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            >
              <div className="pb-3 pt-0.5">
                {this.renderPaletteDeck()}
              </div>
            </div>

            <button
              type="button"
              onClick={this.cyclePalettePage}
              disabled={paletteAnimating}
              className="h-8 w-8 shrink-0 rounded-full border border-slate-700 bg-slate-900/80 text-slate-300 transition hover:border-slate-500 hover:text-white disabled:cursor-default disabled:opacity-70"
              title={`Show color set ${((palettePageIndex + 1) % COLOR_PAGES.length) + 1}`}
              aria-label="Switch color set"
            >
              <RefreshCcw size={14} className={`mx-auto transition-transform duration-200 ${paletteAnimating ? 'rotate-180' : ''}`} />
            </button>

            <div className="ml-auto flex items-center gap-2 shrink-0">
              <Button
                onPress={this.placePixel}
                isDisabled={!canPlaceWithCurrentState || !selectedUnlocked}
                className={[
                  'min-w-[182px] h-auto px-3.5 py-2.5 rounded-2xl border text-white transition-all',
                  'shadow-[0_10px_30px_rgba(30,41,59,0.45)]',
                  canPlaceWithCurrentState && selectedUnlocked
                    ? 'border-indigo-300/40 bg-[linear-gradient(135deg,rgba(79,70,229,0.95),rgba(37,99,235,0.92))] hover:scale-[1.02] hover:shadow-[0_14px_34px_rgba(59,130,246,0.28)]'
                    : 'border-slate-700 bg-[linear-gradient(135deg,rgba(51,65,85,0.95),rgba(30,41,59,0.92))] text-slate-300'
                ].join(' ')}
              >
                <div className="flex w-full items-center justify-between gap-4">
                  <div className="text-left leading-tight">
                    <div className="text-[14px] font-semibold tracking-[0.02em]">
                      {placingPixel ? 'Placing...' : 'Place'}
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-indigo-100/85">
                      {missingPixels > 0 ? this.formatCooldown(msUntilNextPixel) : 'Fully charged'}
                    </div>
                  </div>
                  <div className="rounded-xl border border-white/15 bg-white/10 px-2.5 py-1 text-[13px] font-semibold shadow-inner shadow-white/5">
                    {availablePixels}/{maxPixels}
                  </div>
                </div>
              </Button>
            </div>
          </div>

          {(quickPixelError || helperText || tokenActionError) && (
            <div className="border-t border-slate-800 px-4 py-2 text-xs">
              {quickPixelError ? (
                <div className="text-red-300">{quickPixelError}</div>
              ) : helperText ? (
                <div className="text-amber-300">{helperText}</div>
              ) : (
                <div className="text-red-300">{tokenActionError}</div>
              )}
            </div>
          )}
        </div>
        <LoginRequiredModal
          isOpen={lockedColorModalOpen}
          onClose={() => this.setState({ lockedColorModalOpen: false })}
          message={this.getLockedMessage()}
        />
        <Modal
          isOpen={premiumColorModalOpen}
          onClose={() => this.setState({
            premiumColorModalOpen: false,
            pendingPremiumColor: null,
            premiumColorPurchaseError: null,
          })}
          size="md"
          backdrop="blur"
          placement="center"
        >
          <ModalContent className="bg-slate-900 text-white">
            <ModalHeader className="text-lg font-bold">
              Unlock {pendingPremiumColor?.label ?? 'Premium Color'}
            </ModalHeader>
            <ModalBody>
              <div className="flex items-center gap-3">
                <div
                  className="h-10 w-10 rounded-xl border border-white/15"
                  style={{ backgroundColor: pendingPremiumColor?.hex ?? '#000000' }}
                />
                <div className="text-sm text-slate-300">
                  Buy this premium color for <span className="font-semibold text-white">{this.getPremiumColorCost(pendingPremiumColor).toLocaleString()}</span> Pixel Drops.
                </div>
              </div>
              {premiumColorPurchaseError && (
                <div className="text-sm text-red-300">{premiumColorPurchaseError}</div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                variant="light"
                onPress={() => this.setState({
                  premiumColorModalOpen: false,
                  pendingPremiumColor: null,
                  premiumColorPurchaseError: null,
                })}
              >
                Cancel
              </Button>
              <Button
                className="bg-indigo-600 text-white hover:bg-indigo-500"
                isLoading={premiumColorPurchaseLoading}
                onPress={this.handleBuyPremiumColor}
              >
                Buy for {this.getPremiumColorCost(pendingPremiumColor).toLocaleString()}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <LoginRequiredModal
          isOpen={tokenLoginModalOpen}
          onClose={() => this.setState({ tokenLoginModalOpen: false })}
          message="Log in to get at least 5x more pixels and unlock faster refills."
        />
        <Modal
          isOpen={tokenUseModalOpen}
          onClose={() => this.setState({ tokenUseModalOpen: false })}
          size="md"
          backdrop="blur"
          placement="center"
        >
          <ModalContent className="border border-indigo-400/20 bg-slate-900 text-white shadow-[0_24px_80px_rgba(30,64,175,0.22)]">
            <ModalHeader className="text-lg font-bold">Restore Pixels</ModalHeader>
            <ModalBody>
              <div className="rounded-2xl border border-indigo-400/20 bg-[linear-gradient(135deg,rgba(79,70,229,0.18),rgba(37,99,235,0.12))] p-4 shadow-[0_18px_40px_rgba(30,64,175,0.14)]">
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-indigo-200/80">Restore Tokens</div>
                    <div className="mt-1 text-3xl font-bold text-white">{placeTokenCount}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-indigo-200/80">Missing</div>
                    <div className="mt-1 text-3xl font-bold text-white">{missingPixels}</div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-700 bg-slate-950/80 p-4">
                <div className="text-center">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-slate-400">Restore</div>
                  <div className="mt-1 text-2xl font-bold text-white">
                    {selectedTokenUseCount} pixel{selectedTokenUseCount === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      className="min-w-10 bg-[linear-gradient(135deg,rgba(79,70,229,0.95),rgba(37,99,235,0.92))] text-white"
                      isDisabled={selectedTokenUseCount <= 1}
                      onPress={() => this.setState({ tokenUseCount: 1 })}
                    >
                      {'<<'}
                    </Button>
                    <Button
                      size="sm"
                      className="min-w-10 bg-[linear-gradient(135deg,rgba(79,70,229,0.95),rgba(37,99,235,0.92))] text-white"
                      isDisabled={selectedTokenUseCount <= 1}
                      onPress={() => this.setState({ tokenUseCount: Math.max(1, selectedTokenUseCount - 1) })}
                    >
                      {'<'}
                    </Button>
                    <input
                      type="range"
                      min="1"
                      max={maxTokenSelectable}
                      value={selectedTokenUseCount}
                      onChange={(event) => this.setState({ tokenUseCount: Number(event.target.value) })}
                      className="w-full accent-indigo-500"
                    />
                    <Button
                      size="sm"
                      className="min-w-10 bg-[linear-gradient(135deg,rgba(79,70,229,0.95),rgba(37,99,235,0.92))] text-white"
                      isDisabled={selectedTokenUseCount >= maxTokenSelectable}
                      onPress={() => this.setState({ tokenUseCount: Math.min(maxTokenSelectable, selectedTokenUseCount + 1) })}
                    >
                      {'>'}
                    </Button>
                    <Button
                      size="sm"
                      className="min-w-10 bg-[linear-gradient(135deg,rgba(79,70,229,0.95),rgba(37,99,235,0.92))] text-white"
                      isDisabled={selectedTokenUseCount >= maxTokenSelectable}
                      onPress={() => this.setState({ tokenUseCount: maxTokenSelectable })}
                    >
                      {'>>'}
                    </Button>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
                    <span>1</span>
                    <span>{selectedTokenUseCount}</span>
                    <span>{maxTokenSelectable}</span>
                  </div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={() => this.setState({ tokenUseModalOpen: false })}>
                Cancel
              </Button>
              <Button
                className="bg-[linear-gradient(135deg,rgba(79,70,229,0.95),rgba(37,99,235,0.92))] text-white hover:scale-[1.01]"
                onPress={() => {
                  this.setState({ tokenUseModalOpen: false });
                  window.location.assign('/Shop');
                }}
              >
                Buy More
              </Button>
              <Button
                className="bg-[linear-gradient(135deg,rgba(79,70,229,0.95),rgba(37,99,235,0.92))] text-white hover:scale-[1.01]"
                isLoading={tokenActionLoading}
                onPress={this.handleUsePlaceTokens}
              >
                Restore {selectedTokenUseCount} Pixel{selectedTokenUseCount === 1 ? '' : 's'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <Modal
          isOpen={storePromptOpen}
          onClose={() => this.setState({ storePromptOpen: false })}
          size="md"
          backdrop="blur"
          placement="center"
        >
          <ModalContent className="bg-slate-900 text-white">
            <ModalHeader className="text-lg font-bold">{storePromptTitle}</ModalHeader>
            <ModalBody>
              <p className="text-sm text-slate-300">{storePromptMessage}</p>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={() => this.setState({ storePromptOpen: false })}>
                Close
              </Button>
              <Button
                className="bg-indigo-600 text-white hover:bg-indigo-500"
                onPress={() => {
                  this.setState({ storePromptOpen: false });
                  window.location.assign('/Shop');
                }}
              >
                Go to Store
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
    );
  }

  render() {
    const { mouseX, mouseY, isFullScreen } = this.state;
    return (
      <div className="w-full h-full relative overflow-hidden">
        <div className="absolute top-12 left-1 text-white px-3 py-1 rounded text-sm font-mono z-[800]">
          <div className="text-center bg-slate-900 rounded-md">
            {mouseX !== null && mouseY !== null ? `(${mouseX}, ${mouseY})` : '(-, -)'}
          </div>
        </div>

        <div className={`absolute bottom-8 left-0 z-[800] group transition-all duration-300 ${
          isFullScreen ? 'opacity-50 translate-x-[-85%] hover:translate-x-0 hover:opacity-100' : 'opacity-100 translate-x-0'
        }`}>
          <div className="flex flex-col items-center space-y-3 bg-black bg-opacity-60 p-2 rounded-r-lg">
            <Button onPress={() => this.adjustZoom(1.2)} isIconOnly className="bg-indigo-600 text-white w-10 h-10 text-xl font-bold rounded-full shadow hover:bg-purple-500">+</Button>
            <Button onPress={() => this.adjustZoom(0.8)} isIconOnly className="bg-indigo-600 text-white w-10 h-10 text-xl font-bold rounded-full shadow hover:bg-purple-500">-</Button>
            <Button onPress={this.toggleFullScreen} isIconOnly className="bg-indigo-600 text-white w-10 h-10 text-xl font-bold rounded-full shadow hover:bg-purple-500">
              <Expand />
            </Button>
          </div>
        </div>

        <div
          ref={this.canvasContainerRef}
          className={`${isFullScreen ? 'fixed inset-0 z-[700]' : 'w-[90vw] h-[90vh] mx-auto mt-10'} flex items-center justify-center border border-gray-300 bg-black`}
        >
          <canvas
            ref={this.canvasRef}
            className={`${this.dragging ? 'cursor-grabbing' : 'cursor-crosshair'}`}
            style={{ imageRendering: 'pixelated', display: 'block', touchAction: 'none' }}
          />
        </div>

        {this.renderPlacePanel()}
      </div>
    );
  }
}

export default FreeDrawCanvas;

